import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, FileDown, MessageCircle, FileText, ChevronRight,
  ChevronLeft, ChevronRight as ChevR, ShieldCheck, BadgeCheck, Phone,
  CheckCircle2, Truck, Settings, Award,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { useRFQ } from "../components/RFQProvider";
import { COMPANY } from "../lib/brand";

function asUrl(u) {
  if (!u) return "";
  return u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const { openRFQ } = useRFQ();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  useEffect(() => {
    if (!p) return;
    api.get("/products").then((r) => {
      const others = r.data.filter((x) => x.id !== p.id && x.category === p.category).slice(0, 3);
      setRelated(others);
    });
    setActive(0);
  }, [p]);

  const gallery = useMemo(() => {
    if (!p) return [];
    const list = [];
    if (Array.isArray(p.images) && p.images.length) list.push(...p.images);
    if (p.image_url) list.push(p.image_url);
    return Array.from(new Set(list)).slice(0, 8);
  }, [p]);

  if (p === null) return <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-6 py-20 text-slate-500">Loading…</div>;
  if (p === false) return <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-6 py-20 text-slate-500">Product not found.</div>;

  const cover = gallery[active] || "";
  const waText = encodeURIComponent(`Hi, I am interested in the ${p.name}. Please share more details.`);
  const waHref = `https://wa.me/${COMPANY.whatsapp}?text=${waText}`;
  const specEntries = Object.entries(p.specifications || {});

  return (
    <div data-testid="product-detail-page" className="bg-white">
      {/* TOP GLOW + breadcrumb on dark band */}
      <section className="bg-carbon-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-amber-500/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-brand-500/20 rounded-full blur-3xl" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 pt-6 pb-3 text-xs text-slate-400 font-mono flex items-center gap-2 overflow-x-auto">
          <Link to="/" className="hover:text-amber-400 whitespace-nowrap">Home</Link>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <Link to="/products" className="hover:text-amber-400 whitespace-nowrap">Products</Link>
          <ChevronRight className="w-3 h-3 shrink-0" />
          <span className="text-amber-400 truncate">{p.name}</span>
        </div>
      </section>

      {/* HERO product split */}
      <section className="bg-carbon-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-25" />
        <div className="absolute -top-32 right-1/3 w-[420px] h-[420px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-8 md:py-12 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* GALLERY */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] bg-slate-900 border border-amber-500/20 overflow-hidden group shadow-[0_30px_60px_-20px_rgba(245,158,11,0.25)]">
              {cover ? (
                <img
                  key={active}
                  src={asUrl(cover)}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-slate-500 text-sm">No image</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-carbon-950/30 via-transparent to-transparent pointer-events-none" />
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setActive((a) => (a - 1 + gallery.length) % gallery.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/90 hover:bg-amber-500 hover:text-carbon-950 text-carbon-950 transition-all rounded-full opacity-0 group-hover:opacity-100"
                    aria-label="Previous"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActive((a) => (a + 1) % gallery.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-white/90 hover:bg-amber-500 hover:text-carbon-950 text-carbon-950 transition-all rounded-full opacity-0 group-hover:opacity-100"
                    aria-label="Next"
                    data-testid="gallery-next"
                  >
                    <ChevR className="w-5 h-5" />
                  </button>
                </>
              )}
              {p.featured && (
                <div className="absolute top-3 left-3 bg-amber-500 text-carbon-950 px-3 py-1.5 text-[10px] font-black tracking-widest uppercase rounded-sm shadow-lg">
                  ★ Featured
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-carbon-950/85 backdrop-blur px-2.5 py-1 text-[10px] font-mono font-bold text-amber-400 tracking-wider rounded">
                {String(active + 1).padStart(2, "0")} / {String(Math.max(gallery.length, 1)).padStart(2, "0")}
              </div>
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-2 sm:gap-3">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`aspect-[4/3] overflow-hidden border-2 transition-all ${
                      i === active
                        ? "border-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.25)]"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <img src={asUrl(g)} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO COLUMN */}
          <div className="lg:col-span-5">
            <div className="text-[10.5px] tracking-[0.3em] text-amber-400 font-bold uppercase mb-3 font-mono inline-flex items-center gap-2">
              <span className="h-px w-7 bg-amber-400" /> {p.category}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-[2.7rem] font-extrabold leading-[1.05] tracking-tight mb-4 text-white">
              {p.name}
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-6 max-w-xl">
              {p.short_description}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-2.5 mb-7">
              <Button
                data-testid="product-rfq-btn"
                onClick={() => openRFQ({ product_name: p.name })}
                className="rounded-full btn-amber font-bold uppercase tracking-[0.18em] text-xs h-12 px-6"
              >
                <FileText className="w-4 h-4 mr-2" /> Request Quotation
              </Button>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-wa-btn"
                className="rounded-full h-12 px-6 inline-flex items-center font-bold uppercase tracking-[0.18em] text-xs bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="product-call-btn"
                className="rounded-full h-12 px-6 inline-flex items-center font-bold uppercase tracking-[0.18em] text-xs bg-white/10 text-white hover:bg-white/20 border border-white/30 transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" /> Call Sales
              </a>
              {p.pdf_url ? (
                <a
                  href={asUrl(p.pdf_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-pdf-btn"
                  className="rounded-full h-12 px-6 inline-flex items-center font-bold uppercase tracking-[0.18em] text-xs bg-amber-500/10 text-amber-400 border border-amber-500/40 hover:bg-amber-500/20 transition-colors"
                >
                  <FileDown className="w-4 h-4 mr-2" /> Brochure
                </a>
              ) : null}
            </div>

            {/* Trust pills */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { icon: ShieldCheck, label: "Mining\nSafety" },
                { icon: BadgeCheck, label: "Field\nTested" },
                { icon: Award, label: "DGMS\nAligned" },
              ].map((t, i) => {
                const Ic = t.icon;
                return (
                  <div key={i} className="border border-white/15 p-3 text-center bg-white/5 hover:bg-white/10 transition-colors">
                    <Ic className="w-6 h-6 text-amber-400 mx-auto mb-1.5" strokeWidth={2} />
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300 font-mono leading-tight whitespace-pre-line">
                      {t.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick callout */}
            <div className="border-l-2 border-amber-500 pl-4 py-2">
              <div className="font-mono text-[10px] tracking-[0.2em] text-amber-400 font-bold uppercase">Why this matters</div>
              <p className="text-sm text-slate-300 mt-1">
                Engineered in Bangalore. Tested across 14 mining states. Backed by a 4-hour SLA service network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPTION + AT-A-GLANCE */}
      <section className="bg-white py-14 md:py-16">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Product Overview</div>
            <h2 className="h-section text-carbon mb-5">Engineered for the toughest mining environments.</h2>
            <p className="body-lead text-slate-700 whitespace-pre-line">{p.description}</p>

            {specEntries.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-3">Key Highlights</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {specEntries.slice(0, 6).map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={2.2} />
                      <div className="text-sm">
                        <span className="font-bold text-carbon">{k}: </span>
                        <span className="text-slate-700 font-mono text-[13px]">{v}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SIDE — at-a-glance card */}
          <aside className="lg:col-span-4">
            <div className="card-premium p-6 lg:sticky lg:top-28">
              <div className="overline mb-2">At a glance</div>
              <h3 className="font-heading text-xl font-bold text-carbon mb-5">{p.name}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2 pb-3 border-b border-slate-200">
                  <Truck className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-carbon text-[11px] uppercase tracking-widest font-mono">Application</div>
                    <div className="text-slate-700 mt-0.5">HEMM dumpers, excavators, loaders, dozers</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 pb-3 border-b border-slate-200">
                  <Settings className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-carbon text-[11px] uppercase tracking-widest font-mono">Service</div>
                    <div className="text-slate-700 mt-0.5">Pan-India installation, calibration, AMC</div>
                  </div>
                </div>
                <div className="flex items-start gap-2 pb-3 border-b border-slate-200">
                  <Phone className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-carbon text-[11px] uppercase tracking-widest font-mono">Contact Sales</div>
                    <a href={`tel:${COMPANY.phoneRaw}`} className="block text-slate-700 hover:text-amber-600 font-mono mt-0.5">{COMPANY.phone}</a>
                    <a href={`mailto:${COMPANY.email1}`} className="block text-slate-700 hover:text-amber-600 font-mono">{COMPANY.email1}</a>
                  </div>
                </div>
                <Button
                  onClick={() => openRFQ({ product_name: p.name })}
                  className="w-full mt-3 rounded-full btn-amber font-bold uppercase tracking-[0.18em] text-xs h-12"
                  data-testid="aside-rfq-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Get Quote
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* SPECS TABLE — premium */}
      <section className="bg-slate-50 py-16 md:py-20 border-y border-slate-200">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="overline mb-2">Technical Specifications</div>
              <h2 className="h-section text-carbon">Datasheet</h2>
            </div>
            <Button
              onClick={() => openRFQ({ product_name: p.name })}
              className="rounded-full btn-amber font-bold uppercase tracking-[0.18em] text-xs h-11 px-5"
              data-testid="product-rfq-btn-2"
            >
              Enquire Now
            </Button>
          </div>
          <div className="border border-slate-200 bg-white shadow-[0_22px_44px_-22px_rgba(10,19,34,0.12)]">
            <table className="w-full text-sm">
              <tbody>
                {specEntries.map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="py-3.5 px-5 font-bold text-carbon w-1/3 border-r border-slate-200 align-top text-[13px] uppercase tracking-wider">
                      {k}
                    </td>
                    <td className="py-3.5 px-5 text-slate-700 font-mono text-[13px] align-top">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {specEntries.length === 0 && (
              <div className="p-8 text-slate-500 text-center text-sm">No specifications available.</div>
            )}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
            <div className="overline mb-2">In the same category</div>
            <h2 className="h-section text-carbon mb-8">Related products</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((r) => {
                const cv = (r.images && r.images[0]) || r.image_url;
                return (
                  <Link
                    key={r.id}
                    to={`/products/${r.id}`}
                    data-testid={`related-product-${r.id}`}
                    className="group card-premium block overflow-hidden"
                  >
                    <div className="aspect-[4/3] bg-slate-100">
                      {cv ? (
                        <img src={asUrl(cv)} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] tracking-[0.22em] text-amber-600 font-bold uppercase mb-1.5 font-mono">{r.category}</div>
                      <h3 className="h-card text-carbon group-hover:text-amber-600 transition-colors">{r.name}</h3>
                      <p className="text-xs text-slate-600 line-clamp-2 mt-1.5">{r.short_description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="bg-carbon-950 text-white py-14 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-25" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-amber-400 mb-2">Ready to deploy?</div>
            <h3 className="h-section">Get a technical quote for {p.name} in 24 hours.</h3>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <Button
              onClick={() => openRFQ({ product_name: p.name })}
              className="h-12 rounded-full btn-amber px-7 font-bold uppercase tracking-[0.18em] text-xs"
              data-testid="cta-rfq-btn-bottom"
            >
              <FileText className="w-4 h-4 mr-2" /> Request Quotation
            </Button>
            <Link
              to="/products"
              className="h-12 px-7 inline-flex items-center justify-center font-bold uppercase tracking-[0.18em] text-xs bg-white/10 hover:bg-white/20 text-white border border-white/30 transition-colors rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to all products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
